import { Button, Text } from "@sentricmusic/ui";
import { useNavigate } from "@tanstack/react-router";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useElasticQuery } from "@/api/mutations";
import LoadingSmall from "@/components/LoadingSmall";
import useGetColours from "@/helpers/getColours";
import useAppStore from "@/store/appStateZustand";
import { useUIStateStore } from "@/store/uiStateStore";

import CompontentTabs from "./_componentTabs";
import CumulativeEarnings from "./_cumulativeEarningsAndByPeriod";
import IncomeSection from "./_incomeSection";
import SongsSection from "./_songsSection";
import TabIncome from "./_tabIncome";
import TabSongs from "./_tabSongs";
import TabTerritories from "./_tabTerritories";
import TabWriters from "./_tabWriters";
import TerritorySection from "./_territorySection";
import WritersSection from "./_writersSection";
import { buildRoyaltiesQuery } from "./utils/buildRoyaltiesQueries";
import mapLegacyFiltersToBuildFilters from "./utils/mapLegacyFiltersToBuildFilters";

export default function RoyaltiesData({ appliedFilters }) {
  const navigate = useNavigate();
  const { t } = useTranslation("other");
  const flags = useFlags();
  const royaltiesMaintenanceMode = flags["royalties-maintenance-mode"];
  const showHistoric = flags["show-historic-royalties"];
  const isTunecore = useUIStateStore(state => state.isTunecore);

  const { highlightColour } = useGetColours();
  const highlight = isTunecore ? "#00ef86" : highlightColour;

  const [pending, setPending] = useState(false);
  const [result, setResult] = useState(null);
  const [graph, setGraph] = useState("bar");
  const [dateType] = useState("publishedStatementDate");

  const elasticQueryMutation = useElasticQuery();

  useEffect(() => {
    if (!appliedFilters || royaltiesMaintenanceMode)
      return;

    const { accounts, writers, songs, dateFrom, dateTo } = appliedFilters;
    const fromStr = dateFrom.toISOString().split("T", 1)[0];
    const toStr = dateTo.toISOString().split("T", 1)[0];

    const legacyFilters = mapLegacyFiltersToBuildFilters(accounts, writers, songs);

    useAppStore.setState(() => ({
      range: {
        publishedStatementDate: { format: "strict_date_optional_time", gte: fromStr, lte: toStr },
        receiptDate: { format: "strict_date_optional_time", gte: fromStr, lte: toStr },
      },
      royaltiesFilter: legacyFilters,
    }));

    const query = buildRoyaltiesQuery({ accounts, writers, songs, dateFrom, dateTo, dateType, showHistoric });
    setPending(true);
    setResult(null);
    elasticQueryMutation.mutate(query, {
      onSuccess: (data) => {
        setPending(false);
        setResult(data);
      },
      onError: () => {
        setPending(false);
        setResult(null);
      },
    });
  }, [appliedFilters, royaltiesMaintenanceMode, dateType, showHistoric]);

  if (royaltiesMaintenanceMode) {
    return (
      <div className="p-4">
        <div className="relative flex flex-col px-4 py-3 text-red-700 bg-red-100 border border-red-400 rounded-lg" role="alert">
          <strong className="font-bold">{t("other:maintenance-mode1")}</strong>
          <span className="block sm:inline">{t("other:maintenance-mode2")}</span>
        </div>
      </div>
    );
  }

  // Pre-search prompt — shown before the user clicks Search for the first time
  if (!appliedFilters) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Text as="subtitle" className="mb-2">
          {t("other:royalties-search-prompt-title", "Select filters and search to view royalties")}
        </Text>
        <Text as="p">
          {t("other:royalties-search-prompt", "Choose your accounts, writers, songs and a date range, then click Search.")}
        </Text>
      </div>
    );
  }

  if (pending) {
    return (
      <div className="flex items-center justify-center w-full min-h-96">
        <div id="bars">
          {Array.from({ length: 10 }).map((_, i) => <div key={i} className="bar" />)}
        </div>
      </div>
    );
  }

  const totalEarnings = result?.body?.aggregations?.WriterPaymentSum?.value || 0;
  const songCount = result?.body?.aggregations?.Songs?.buckets?.length || 0;
  const hasRoyaltiesData = totalEarnings > 0 || songCount > 0;

  if (!hasRoyaltiesData) {
    return (
      <div className="rounded-lg mt-8 p-4 lg:p-8">
        <div className="flex flex-col">
          <Text as="subtitle" className="mb-2">
            {t("other:no-royalties-title", "No Royalties Data Available")}
          </Text>
          <Text>{t("other:no-royalties-message", "You will see data here once you have received your first statement.")}</Text>
          <Text as="bodySm" className="text-xs mt-2">
            {t("other:no-royalties-support-message", "If this seems like we've made a mistake, please reach out to support.")}
          </Text>
          <Button
            className="self-start mt-4 w-max"
            variant={isTunecore ? "primary" : "secondary"}
            label={t("other:go-to-statements", "Go To Statements")}
            onClick={() => navigate({ to: "/statements" })}
          />
        </div>
      </div>
    );
  }

  const scopedAccounts = appliedFilters?.accounts ?? [];

  return (
    <div>
      <div className="relative">
        <div className="absolute right-0 -top-6">
          {pending && <LoadingSmall />}
        </div>
      </div>

      <div className="flex items-center border-b-2 border-gray-200 h-14">
        <button
          type="button"
          onClick={() => setGraph("bar")}
          style={{ borderColor: graph === "bar" ? highlight : "transparent" }}
          className={`${isTunecore ? "text-white" : "text-ink-secondary"} px-3 uppercase text-xs font-semibold flex items-center border-b-4 h-14 -mb-1 focus:outline-none`}
        >
          {t("other:earnings-by-period")}
        </button>
        <button
          type="button"
          onClick={() => setGraph("line")}
          style={{ borderColor: graph === "line" ? highlight : "transparent" }}
          className={`${isTunecore ? "text-white" : "text-ink-secondary"} px-3 uppercase text-xs font-semibold flex items-center border-b-4 h-14 -mb-1 focus:outline-none`}
        >
          {t("other:cumulative-earnings")}
        </button>
      </div>

      <CumulativeEarnings
        data={result}
        dateType={dateType}
        pending={pending}
        graph={graph}
      />

      <CompontentTabs
        tabs={[
          <TabSongs key="tab-songs" pending={pending} result={result} />,
          <TabWriters key="tab-writers" pending={pending} result={result} />,
          <TabIncome key="tab-income" pending={pending} result={result} />,
          <TabTerritories key="tab-territories" pending={pending} result={result} />,
        ]}
        objects={[
          <SongsSection key="songs-section" dateType={dateType} matchAccounts={scopedAccounts} />,
          <WritersSection key="writers-section" dateType={dateType} matchAccounts={scopedAccounts} />,
          <IncomeSection key="income-section" dateType={dateType} matchAccounts={scopedAccounts} />,
          <TerritorySection key="territory-section" dateType={dateType} matchAccounts={scopedAccounts} />,
        ]}
      />
    </div>
  );
}
